import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getEventRegistrations, getOrganizerPlayers, deleteRegistration } from '../../services/api';
import toast from 'react-hot-toast';

const RegistrationsModal = ({ event, isOpen, onClose, organizerId }) => {
    // 1. Get registrations for this event
    const { data: registrations = [], isLoading: isLoadingRegs } = useQuery({
        queryKey: ['event-registrations', event?.id],
        queryFn: () => getEventRegistrations(event.id),
        enabled: !!event?.id && isOpen
    });

    // 2. Get organizer's players to match phone numbers
    const { data: playersData, isLoading: isLoadingPlayers } = useQuery({
        queryKey: ['organizer-players', organizerId],
        queryFn: () => getOrganizerPlayers(organizerId),
        enabled: !!organizerId && isOpen
    });

    const queryClient = useQueryClient();
    const players = playersData?.players || [];

    const deleteRegMutation = useMutation({
        mutationFn: (id) => deleteRegistration(id),
        onSuccess: () => {
            queryClient.invalidateQueries(['event-registrations', event?.id]);
            queryClient.invalidateQueries(['my-events', organizerId]);
            toast.success('Inscripción eliminada correctamente');
        },
        onError: (err) => {
            console.error("Error deleting registration:", err);
            toast.error('No se pudo eliminar la inscripción');
        }
    });

    const handleDeleteRegistration = (reg) => {
        if (window.confirm(`¿Estás seguro de que deseas desapuntar a ${reg.player1Name} de este pozo?`)) {
            deleteRegMutation.mutate(reg.id);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <div 
                className="bg-white rounded-[2rem] sm:rounded-[2.5rem] shadow-2xl w-full max-w-2xl max-h-[90vh] sm:max-h-[85vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-5 sm:p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <div>
                        <h3 className="font-black text-gray-900 uppercase tracking-widest italic text-lg sm:text-xl">
                            Listado de Inscritos
                        </h3>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-1 flex items-center gap-2">
                            <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></span>
                            {event?.title}
                        </p>
                    </div>
                    <button 
                        onClick={onClose}
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-white border border-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-900 hover:shadow-xl hover:shadow-gray-100 transition-all active:scale-90"
                    >
                        <span className="text-xl">✕</span>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-5 sm:p-8 custom-scrollbar">
                    {isLoadingRegs || isLoadingPlayers ? (
                        <div className="flex flex-col items-center justify-center py-10 sm:py-20 gap-4">
                            <div className="w-10 h-10 border-4 border-primary-500/20 border-t-primary-500 rounded-full animate-spin"></div>
                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Cargando inscritos...</p>
                        </div>
                    ) : registrations.length === 0 ? (
                        <div className="text-center py-20 px-6 sm:px-10">
                            <div className="text-4xl mb-4 opacity-20">👥</div>
                            <h4 className="text-lg font-black text-gray-900 uppercase tracking-tight">No hay inscritos todavía</h4>
                            <p className="text-sm text-gray-500 font-medium max-w-xs mx-auto mt-2">
                                Comparte el enlace del pozo para que los jugadores empiecen a inscribirse.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {registrations.map((reg) => {
                                const player = players.find(p => p.id === reg.player1Id);
                                const phone = player?.phone || reg.phone || null;
                                
                                return (
                                    <div 
                                        key={reg.id} 
                                        className="flex flex-col sm:flex-row sm:items-center justify-between p-4 sm:p-5 bg-gray-50/50 rounded-2xl border border-transparent hover:border-gray-100 hover:bg-white transition-all group"
                                    >
                                        <div className="flex items-center gap-3 sm:gap-4">
                                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary-100 flex items-center justify-center text-primary-600 font-black text-base sm:text-lg">
                                                {reg.player1Name?.[0]?.toUpperCase() || 'P'}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <h4 className="font-black text-gray-900 uppercase text-xs sm:text-sm tracking-tight group-hover:text-primary-600 transition-colors truncate">
                                                    {reg.player1Name}
                                                </h4>
                                                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-0.5 sm:mt-1">
                                                    <span className={`text-[9px] sm:text-[10px] font-black uppercase px-1.5 sm:px-2 py-0.5 rounded-lg border ${
                                                        reg.paymentStatus === 'pagado' 
                                                        ? 'bg-emerald-50 text-emerald-600 border-emerald-100' 
                                                        : 'bg-amber-50 text-amber-600 border-amber-100 animate-pulse'
                                                    }`}>
                                                        {reg.paymentStatus === 'pagado' ? '✅ Pagado' : '⏳ Pendiente'}
                                                    </span>
                                                    <span className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-widest bg-gray-100/50 px-1.5 sm:px-2 py-0.5 rounded-lg">
                                                        {reg.paymentMethod || 'App'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 mt-3 sm:mt-0 pt-3 sm:pt-0 border-t sm:border-t-0 border-gray-100">
                                            {phone ? (
                                                <a 
                                                    href={`https://wa.me/${phone.replace(/[^0-9]/g, '')}`} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-emerald-500/30 hover:bg-emerald-600 hover:-translate-y-0.5 transition-all active:scale-95"
                                                    title="Enviar WhatsApp"
                                                >
                                                    <span className="text-base text-white">💬</span>
                                                    <span className="hidden sm:inline">WhatsApp</span>
                                                    <span className="sm:hidden">WhatsApp</span>
                                                </a>
                                            ) : null}
                                            
                                            <button 
                                                onClick={() => handleDeleteRegistration(reg)}
                                                className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-red-50 text-red-500 rounded-xl hover:bg-red-100 transition-all active:scale-90"
                                                title="Desapuntar jugador"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-5 sm:p-8 bg-gray-50/50 border-t border-gray-100">
                    <div className="flex items-center justify-between text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">
                        <span>Total Inscritos: {registrations.length}</span>
                        <span>VisioPadel 2026</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegistrationsModal;
